import {all, call, put, takeLatest} from "redux-saga/effects";
import {
  createPlane, deletePlane, editPlane,
  getPlaneList,
  setCreatePlaneResult, setDeletePlaneResult, setEditPlaneResult,
  setPlaneListResult, setSubmitPlaneForReviewResult, submitPlaneForReview,
  getPlaneApprovalList, setPlaneApprovalListResult,
  declinePlane, setDeclinePlaneResult,
  confirmPlane, setConfirmPlaneResult
} from "../../reducers/fleetReducer/fleetReducer";
import {PayloadAction} from "@reduxjs/toolkit";
import {
  ICreatePlanePayload, IDeletePlanePayload,
  IEditPlanePayload,
  IGetPlaneListPayload, ISubmitPlaneForReviewPayload,
  IGetPlaneApprovalListPayload, IDeclinePlanePayload, IConfirmPlanePayload
} from "../../reducers/fleetReducer/fleetReducerTypes";
import {UploadFileResponseType} from "../../../utils/helpers/fileHelpers/fileTypes";
import {getFileInfoAfterUpload} from "../../../utils/helpers/fileHelpers/getFileInfoAfterUpload";
import {
  AirportEntity,
  BookingApiPlaneListRequest,
  BookingApiPlaneListResponse,
  BookingApiPlaneRequest,
  BookingApiPlaneResponse,
  BookingApiPlaneSubmitForReviewRequest,
  BookingApiPlaneDeclineRequest,
  BookingApiPlaneConfirmRequest,
  BookingApiPlaneApprovalListRequest,
  BookingApiPlaneApprovalListResponse,
  CurrencyEntity,
  FileEntity,
  PageModel,
  PlaneEntity,
  PlaneTypeEntity
} from "../../../proto/generated/api_entities_pb";
import {API} from "../../../API/API";
import {getErrorMessageByCode} from "../../../utils/constans/errorCodes/errorCodes";
import {PageModelLimits} from "../../../utils/constans/pagination/pageModelLimits";
import {Logger} from "../../../utils/logger/Logger";
import {getErrorMessage} from "../../../utils/errorHelpers/getCommonErrorMessage";
import {getLocalDateAsISOString} from "../../../utils/helpers/dateHelpers/dateHelpers";
import {metricsGoals} from "../../../utils/constans/metrics/goals";
import {amplitudeEvents} from "../../../utils/analytics/amplitude";


function* getPlaneListSaga({payload}: PayloadAction<IGetPlaneListPayload>) {
  const req = new BookingApiPlaneListRequest()
    .setPagemodel(
      new PageModel()
        .setPage(payload.page)
        .setLimit(PageModelLimits.Planes)
      //TODO add query
    )
    .setQuery(payload.query);

  try {
    const res: BookingApiPlaneListResponse = yield call(API.getPlaneList, req);

    yield put(setPlaneListResult({
      items: res.getPlanesList(),
      totalCount: res.getTotalcount(),
    }));
  } catch (e) {
    Logger.error(getErrorMessage(e))
  }
}


function* createPlaneSaga({payload}: PayloadAction<ICreatePlanePayload>) {
  try {
    const imageUploadedFiles: UploadFileResponseType[] = [];

    payload.imageFiles?.forEach(async (f) => {
      const uploadedFile = await getFileInfoAfterUpload(f)
      imageUploadedFiles.push(uploadedFile);
    })

    const insuranceFile: UploadFileResponseType = yield call(getFileInfoAfterUpload, payload.insuranceFile);
    const certificateFile: UploadFileResponseType = yield call(getFileInfoAfterUpload, payload.certificateFile);
    const etopsFile: UploadFileResponseType | undefined = payload.etopsFile ? yield call(getFileInfoAfterUpload, payload.etopsFile) : undefined;

    const planeEntity = new PlaneEntity()
      .setPlanetype(new PlaneTypeEntity().setPlanetypeid(payload.planeTypeId))
      .setBaseairport(new AirportEntity().setAirportid(payload.baseAirportId))
      .setRegistrationnumber(payload.registrationNumber)
      .setManufacturedate(getLocalDateAsISOString(payload.manufactureDate))
      .setMaxpassengers(payload.maxPassengers)
      .setMaxdistance(payload.maxDistance)
      .setIsanimals(payload.isAnimals)
      .setIscargo(payload.isCargo)
      .setIssmoking(payload.isSmoking)
      .setIsambulance(payload.isAmbulance)
      .setIswifi(payload.isWifi)
      .setIsovenaboard(payload.isOvenAboard)
      .setInsurance(
        new FileEntity()
          .setFileid(insuranceFile.fileId)
          .setName(insuranceFile.fileName)
          .setFilepath(insuranceFile.filePath)
      )
      .setCertificate(
        new FileEntity()
          .setFileid(certificateFile.fileId)
          .setName(certificateFile.fileName)
          .setFilepath(certificateFile.filePath)
      );

    if (etopsFile) {
      planeEntity.setEtops(
        new FileEntity()
          .setFileid(etopsFile.fileId)
          .setName(etopsFile.fileName)
          .setFilepath(etopsFile.filePath)
      );
    }

    planeEntity.setImagesList(
        imageUploadedFiles.map(imageFile => new FileEntity()
          .setFileid(imageFile.fileId)
          .setName(imageFile.fileName)
          .setFilepath(imageFile.filePath)
        )
      )
      .setFlyhourprice(payload.flyHourPrice)
      .setCurrency(new CurrencyEntity().setCurrencyid(payload.flyHourCurrencyId))
      .setPlanecalculatorsettingsList(payload.settingEntities);

    if (payload.renovationDate)
      planeEntity.setRenovationdate(getLocalDateAsISOString(payload.renovationDate))

    if (payload.description)
      planeEntity.setDescription(payload.description)

    const req = new BookingApiPlaneRequest()
      .setPlane(
        planeEntity
      )

    const res: BookingApiPlaneResponse = yield call(API.planeCreate, req);

    if (res.getResultresponse()?.getIssuccessful()) {
      yield put(setCreatePlaneResult({
        item: res.getPlane(),
      }));

      metricsGoals.planeCreated();
    } else {
      yield put(setCreatePlaneResult({
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
      }));
    }
  } catch (e) {
    Logger.error(getErrorMessage(e))
  }
}


function* editPlaneSaga({payload}: PayloadAction<IEditPlanePayload>) {
  try {
    const imageUploadedFiles: UploadFileResponseType[] = [];
    for (const image of payload.imageFiles) {
      const uploadedFile: UploadFileResponseType = yield call(getFileInfoAfterUpload, image)
      imageUploadedFiles.push(uploadedFile);
    }

    const insuranceFile: UploadFileResponseType = yield call(getFileInfoAfterUpload, payload.insuranceFile);
    const certificateFile: UploadFileResponseType = yield call(getFileInfoAfterUpload, payload.certificateFile);
    const etopsFile: UploadFileResponseType | undefined = payload.etopsFile ? yield call(getFileInfoAfterUpload, payload.etopsFile) : undefined;

    const planeEntity = new PlaneEntity()
      .setPlaneid(payload.id)
      .setPlanetype(new PlaneTypeEntity().setPlanetypeid(payload.planeTypeId))
      .setBaseairport(new AirportEntity().setAirportid(payload.baseAirportId))
      .setRegistrationnumber(payload.registrationNumber)
      .setManufacturedate(getLocalDateAsISOString(payload.manufactureDate))
      .setMaxpassengers(payload.maxPassengers)
      .setMaxdistance(payload.maxDistance)
      .setIsanimals(payload.isAnimals)
      .setIscargo(payload.isCargo)
      .setIssmoking(payload.isSmoking)
      .setIsambulance(payload.isAmbulance)
      .setIswifi(payload.isWifi)
      .setIsovenaboard(payload.isOvenAboard)
      .setInsurance(
        new FileEntity()
          .setFileid(insuranceFile.fileId)
          .setName(insuranceFile.fileName)
          .setFilepath(insuranceFile.filePath)
      )
      .setCertificate(
        new FileEntity()
          .setFileid(certificateFile.fileId)
          .setName(certificateFile.fileName)
          .setFilepath(certificateFile.filePath)
      );

    if (etopsFile) {
      planeEntity.setEtops(
        new FileEntity()
          .setFileid(etopsFile.fileId)
          .setName(etopsFile.fileName)
          .setFilepath(etopsFile.filePath)
      );
    }

    planeEntity.setImagesList(
        imageUploadedFiles.map(imageFile => new FileEntity()
          .setFileid(imageFile.fileId)
          .setName(imageFile.fileName)
          .setFilepath(imageFile.filePath)
        )
      )
      .setFlyhourprice(payload.flyHourPrice)
      .setCurrency(new CurrencyEntity().setCurrencyid(payload.flyHourCurrencyId))
      .setPlanecalculatorsettingsList(payload.settingEntities);

    if (payload.renovationDate)
      planeEntity.setRenovationdate(getLocalDateAsISOString(payload.renovationDate))

    if (payload.description)
      planeEntity.setDescription(payload.description)

    const req = new BookingApiPlaneRequest()
      .setPlane(
        planeEntity
      )

    const res: BookingApiPlaneResponse = yield call(API.planeUpdate, req);

    if (res.getResultresponse()?.getIssuccessful()) {
      amplitudeEvents.planeChanged({ planeId: payload.id });
      yield put(setEditPlaneResult({
        item: res.getPlane(),
      }));
    } else {
      yield put(setEditPlaneResult({
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
      }));
    }
  } catch (e) {
    Logger.error(getErrorMessage(e))
  }
}


function* deletePlaneSaga({payload}: PayloadAction<IDeletePlanePayload>) {
  const req = new BookingApiPlaneRequest()
    .setPlane(
      new PlaneEntity()
        .setPlaneid(payload.itemId)
    );

  try {
    const res: BookingApiPlaneResponse = yield call(API.planeDelete, req);

    if (res.getResultresponse()?.getIssuccessful()) {
      yield put(setDeletePlaneResult({
        itemId: payload.itemId,
      }));
    } else {
      yield put(setDeletePlaneResult({
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
      }));
    }
  } catch (e) {
    Logger.error(getErrorMessage(e))
  }
}


function* submitPlaneForReviewSaga({payload}: PayloadAction<ISubmitPlaneForReviewPayload>) {
  const req = new BookingApiPlaneSubmitForReviewRequest()
    .setPlaneid(payload.planeId);

  try {
    const res: BookingApiPlaneResponse = yield call(API.planeSubmitForReview, req);

    if (res.getResultresponse()?.getIssuccessful()) {
      yield put(setSubmitPlaneForReviewResult({
        isSuccess: true,
      }));
    } else {
      yield put(setSubmitPlaneForReviewResult({
        isSuccess: false,
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
      }));
    }
  } catch (e) {
    yield put(setSubmitPlaneForReviewResult({
      isSuccess: false,
      errorMessage: getErrorMessage(e),
    }));
    Logger.error(getErrorMessage(e))
  }
}

function* getPlaneApprovalListSaga({payload}: PayloadAction<IGetPlaneApprovalListPayload>) {
  const req = new BookingApiPlaneApprovalListRequest()
    .setIspending(payload.isPending)
    .setPagemodel(
      new PageModel()
        .setPage(payload.page)
        .setLimit(PageModelLimits.Planes)
    );

  try {
    const res: BookingApiPlaneApprovalListResponse = yield call(API.getPlaneApprovalList, req);
    yield put(setPlaneApprovalListResult({
      items: res.getPlanesList(),
      totalCount: res.getTotalcount(),
    }));
  } catch (e) {
    Logger.error(getErrorMessage(e))
  }
}

function* declinePlaneSaga({payload}: PayloadAction<IDeclinePlanePayload>) {
  const req = new BookingApiPlaneDeclineRequest()
    .setPlaneid(payload.planeId);

  try {
    const res: BookingApiPlaneResponse = yield call(API.planeDecline, req);

    if (res.getResultresponse()?.getIssuccessful()) {
      yield put(setDeclinePlaneResult({
        isSuccess: true,
      }));
    } else {
      yield put(setDeclinePlaneResult({
        isSuccess: false,
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
      }));
    }
  } catch (e) {
    yield put(setDeclinePlaneResult({
      isSuccess: false,
      errorMessage: getErrorMessage(e),
    }));
    Logger.error(getErrorMessage(e))
  }
}

function* confirmPlaneSaga({payload}: PayloadAction<IConfirmPlanePayload>) {
  const req = new BookingApiPlaneConfirmRequest()
    .setPlaneid(payload.planeId);

  if (payload.validUntil) {
    req.setValiduntil(payload.validUntil);
  }

  try {
    const res: BookingApiPlaneResponse = yield call(API.planeConfirm, req);

    if (res.getResultresponse()?.getIssuccessful()) {
      yield put(setConfirmPlaneResult({
        isSuccess: true,
      }));
    } else {
      yield put(setConfirmPlaneResult({
        isSuccess: false,
        errorMessage: getErrorMessageByCode(res.getResultresponse()?.getErrormessage()),
      }));
    }
  } catch (e) {
    yield put(setConfirmPlaneResult({
      isSuccess: false,
      errorMessage: getErrorMessage(e),
    }));
    Logger.error(getErrorMessage(e))
  }
}

export function* fleetSaga() {
  yield all([
    takeLatest(getPlaneList.type, getPlaneListSaga),
    takeLatest(createPlane.type, createPlaneSaga),
    takeLatest(editPlane.type, editPlaneSaga),
    takeLatest(deletePlane.type, deletePlaneSaga),
    takeLatest(submitPlaneForReview.type, submitPlaneForReviewSaga),
    takeLatest(getPlaneApprovalList.type, getPlaneApprovalListSaga),
    takeLatest(declinePlane.type, declinePlaneSaga),
    takeLatest(confirmPlane.type, confirmPlaneSaga),
  ])
}